import { compareAsc } from 'date-fns';
import { ACCESS_REQUESTS_PAGE_SIZE } from '@frameio/access-requests/constants';
import { GetAccessRequests } from '@frameio/access-requests/gql/queries/generated/GetAccessRequests';
import { PAGE_SIZE } from '@frameio/notifications/constants';
import type { GetUnopenedNotificationGroupsCountQuery } from '@frameio/notifications/gql';
import {
  GetNotificationGroups,
  GetUnopenedNotificationGroupsCount,
  useGetNotificationGroupSubscription,
  useUnopenedAccessRequestsCountSubscription,
} from '@frameio/notifications/gql';
import { modifyCachedNotificationGroupsCount } from '@frameio/notifications/utils';
import { reportError } from '@frameio/telemetry';

type AccountNotificationGroupEventsSubscriptionProps = {
  accountId: string;
};

export const AccountNotificationGroupEventsSubscription = ({
  accountId,
}: AccountNotificationGroupEventsSubscriptionProps) => {
  useGetNotificationGroupSubscription({
    variables: {
      scope: {
        unopenedCountUpdate: {
          accountId,
        },
      },
    },
    skip: !accountId,
    onData: async ({ client, data }) => {
      const existingUnopenedCountQuery = client.cache.readQuery({
        query: GetUnopenedNotificationGroupsCount,
        variables: {
          accountId,
        },
      });

      const existingTotalCount = (
        existingUnopenedCountQuery as GetUnopenedNotificationGroupsCountQuery
      )?.account?.notificationGroups?.totalCount;

      const incomingTotalCount =
        data?.data?.notificationGroup?.notificationGroups?.totalCount;

      const existingTimestamp = new Date(
        (existingUnopenedCountQuery as GetUnopenedNotificationGroupsCountQuery)
          ?.account?.notificationGroups?.timestamp
      );
      const incomingTimestamp = new Date(
        data?.data?.notificationGroup?.timestamp
      );

      /* https://date-fns.org/v2.30.0/docs/compareAsc **/
      const result = compareAsc(existingTimestamp, incomingTimestamp);

      if (result !== -1) {
        return;
      }

      /** Automatically refetch notifications if a User already opened the
       * notifications sheet in another client:
       * 1. user has 1 new notification and has Frame.io open in tabs A and B
       * 2. user opens notifications sheet in tab A:
       *      - Notifications are refetched in tab A to display the latest
       *        notifications
       *      - NotificationGroup subscription is sent to tab B with
       *        `totalCount` set to 0
       * 3. notifications should be refetched automatically in tab B so all
       *    clients are in sync.
       */
      const shouldRefetchNotifications =
        existingTotalCount > 0 && incomingTotalCount === 0;

      /** refetches notifications WITHOUT access requests */
      if (shouldRefetchNotifications) {
        await client.query({
          query: GetNotificationGroups,
          variables: {
            accountId,
            page: {
              first: PAGE_SIZE,
            },
          },
          fetchPolicy: 'network-only',
        });
      }

      modifyCachedNotificationGroupsCount({
        accountId,
        client,
        timestamp: data?.data?.notificationGroup?.timestamp,
        totalCount:
          data?.data?.notificationGroup?.notificationGroups?.totalCount,
      });
    },
    onError: (error) => {
      reportError(error, { message: 'GraphQL subscription error' });
    },
  });

  /**
   * This subscription checks the incoming unopened notifications ONLY for the
   * type `NotificationGroupAccessRequestSubmitted`. When present, we refetch the
   * access requests, and use the nodes for display on the FE.
   * */
  useUnopenedAccessRequestsCountSubscription({
    variables: {
      scope: {
        unopenedCountUpdate: {
          accountId,
        },
      },
    },
    skip: !accountId,
    onData: async ({ client, data }) => {
      const incomingTotalCount =
        data?.data?.notificationGroup?.notificationGroups?.totalCount;

      /**
       * Similar to the notification subscription above, refetch access
       * requests when the incoming total is 0, to keep notifications
       * across all tabs and clients in sync.
       */
      const shouldRefetchAccessRequests =
        typeof incomingTotalCount === 'number' && incomingTotalCount === 0;

      if (shouldRefetchAccessRequests) {
        await client.query({
          query: GetAccessRequests,
          variables: {
            accountId,
            page: {
              first: ACCESS_REQUESTS_PAGE_SIZE,
            },
          },
          fetchPolicy: 'network-only',
        });
      }
    },
    onError: (error) => {
      reportError(error, { message: 'GraphQL subscription error' });
    },
  });

  return null;
};
